// Hierarchical query key definitions for React Query cache management
//
// Wave 2 (2026-05-05): every recipients-domain factory carries `mode` (ApiMode)
// as the FIRST positional arg so toggling Live↔Demo invalidates the right cache
// entries without needing a global `invalidateQueries()` hammer.
// Pattern mirrors components/jit-training/hooks/query-keys.ts.



export const recipientKeys = {
  all: () => ['recipients'] as const,
  lists: () => [...recipientKeys.all(), 'list'] as const,
  list: (filter?: Record<string, unknown>) =>
    [...recipientKeys.lists(), filter] as const,
  details: () => [...recipientKeys.all(), 'detail'] as const,
  detail: (id: string) =>
    [...recipientKeys.details(), id] as const,
  metricsAll: () =>
    [...recipientKeys.all(), 'metrics'] as const,
  metrics: (domainId?: string) =>
    [...recipientKeys.all(), 'metrics', domainId ?? null] as const,
  filterOptions: () =>
    [...recipientKeys.all(), 'filter-options'] as const,
  duplicates: () =>
    [...recipientKeys.all(), 'duplicates'] as const,
  count: (filter?: Record<string, unknown>) =>
    [...recipientKeys.all(), 'count', filter] as const,
};

export const tagKeys = {
  all: () => ['tags'] as const,
  lists: () => [...tagKeys.all(), 'list'] as const,
  list: (filter?: Record<string, unknown>) =>
    [...tagKeys.lists(), filter] as const,
  detail: (id: string) =>
    [...tagKeys.all(), 'detail', id] as const,
};

export const groupKeys = {
  all: () => ['groups'] as const,
  lists: () => [...groupKeys.all(), 'list'] as const,
  list: (filter?: Record<string, unknown>) =>
    [...groupKeys.lists(), filter] as const,
  detail: (id: string) =>
    [...groupKeys.all(), 'detail', id] as const,
  members: (id: string) =>
    [...groupKeys.all(), 'members', id] as const,
  // Prefix key — invalidate EVERY filtered metrics query (all filter variants)
  // in one call. Mutations use this; `metrics(filter)` is the per-filter key the
  // KPI-card query subscribes to.
  metricsAll: () => [...groupKeys.all(), 'metrics'] as const,
  metrics: (filter?: Record<string, unknown>) =>
    [...groupKeys.metricsAll(), filter] as const,
};

export const customFieldKeys = {
  all: () => ['custom-fields'] as const,
  lists: () => [...customFieldKeys.all(), 'list'] as const,
  list: () => [...customFieldKeys.lists()] as const,
  detail: (id: string) =>
    [...customFieldKeys.all(), 'detail', id] as const,
  metrics: () => [...customFieldKeys.all(), 'metrics'] as const,
};

export const customFieldPresetKeys = {
  all: () => ['recipient-custom-field-presets'] as const,
  lists: () => [...customFieldPresetKeys.all(), 'list'] as const,
  list: () => [...customFieldPresetKeys.lists()] as const,
  detail: (id: string) =>
    [...customFieldPresetKeys.all(), 'detail', id] as const,
};

export const importKeys = {
  all: () => ['imports'] as const,
  history: () => [...importKeys.all(), 'history'] as const,
  status: (id: string) =>
    [...importKeys.all(), 'status', id] as const,
};

export const auditKeys = {
  all: () => ['audit'] as const,
  lists: () => [...auditKeys.all(), 'list'] as const,
  list: (filter?: Record<string, unknown>) =>
    [...auditKeys.lists(), filter] as const,
  metrics: () => [...auditKeys.all(), 'metrics'] as const,
};

// Workspace-wide, cross-module audit log (Settings → Audit Logs,
// /v1/audit-logs). Distinct namespace from `auditKeys` (which is the
// recipients-scoped log) so the two caches never collide. `filterOptions`
// covers the modules/actions/users dropdown endpoints.
export const workspaceAuditKeys = {
  all: () => ['workspace-audit'] as const,
  lists: () => [...workspaceAuditKeys.all(), 'list'] as const,
  list: (filter?: Record<string, unknown>) =>
    [...workspaceAuditKeys.lists(), filter] as const,
  metrics: () => [...workspaceAuditKeys.all(), 'metrics'] as const,
  modules: () => [...workspaceAuditKeys.all(), 'modules'] as const,
  actions: () => [...workspaceAuditKeys.all(), 'actions'] as const,
  users: () => [...workspaceAuditKeys.all(), 'users'] as const,
};

// Workspace-wide outbound email log (Settings → Email Logs, /v1/email-logs).
// Distinct namespace from `workspaceAuditKeys` — this is the mail-delivery
// record (subject/recipient/transport/body), not the change-audit trail.
// `detail(id)` is keyed per-row because the list omits the (heavy) body.
export const workspaceEmailKeys = {
  all: () => ['workspace-email'] as const,
  lists: () => [...workspaceEmailKeys.all(), 'list'] as const,
  list: (filter?: Record<string, unknown>) =>
    [...workspaceEmailKeys.lists(), filter] as const,
  metrics: () => [...workspaceEmailKeys.all(), 'metrics'] as const,
  providers: () => [...workspaceEmailKeys.all(), 'providers'] as const,
  modules: () => [...workspaceEmailKeys.all(), 'modules'] as const,
  details: () => [...workspaceEmailKeys.all(), 'detail'] as const,
  detail: (id: string) => [...workspaceEmailKeys.details(), id] as const,
};

export const suppressionKeys = {
  all: () => ['suppression'] as const,
  lists: () => [...suppressionKeys.all(), 'list'] as const,
  list: (filter?: Record<string, unknown>) =>
    [...suppressionKeys.lists(), filter] as const,
  metrics: () => [...suppressionKeys.all(), 'metrics'] as const,
};

export const stagingKeys = {
  all: () => ['staging'] as const,
  list: (filter?: Record<string, unknown>) =>
    [...stagingKeys.all(), 'list', filter] as const,
  pendingCount: () =>
    [...stagingKeys.all(), 'pending-count'] as const,
};

export const dataQualityKeys = {
  all: () => ['data-quality'] as const,
  metrics: () => [...dataQualityKeys.all(), 'metrics'] as const,
  incompleteProfiles: () => [...dataQualityKeys.all(), 'incomplete'] as const,
  fieldCoverage: () => [...dataQualityKeys.all(), 'field-coverage'] as const,
};

export const settingsKeys = {
  all: () => ['settings'] as const,
  recipients: () => [...settingsKeys.all(), 'recipients'] as const,
  // Recipients > Settings > AI Chat (/settings/ai-chat) + Tooltip
  // (/settings/tooltip) config. Mode-keyed so a demo session reads the demo
  // workspace's settings, not the real workspace's (and vice versa).
  aiChat: () => [...settingsKeys.all(), 'ai-chat'] as const,
  tooltip: () => [...settingsKeys.all(), 'tooltip'] as const,
};

// Recipients > Org Chart. Centralized here (was a local non-mode factory in
// use-org-chart.ts) so the conformance guard enforces mode-keying.
export const orgChartKeys = {
  all: () => ['org-chart'] as const,
  tree: () => [...orgChartKeys.all(), 'tree'] as const,
  subtree: (id: string) => [...orgChartKeys.all(), 'subtree', id] as const,
  search: (q: string) => [...orgChartKeys.all(), 'search', q] as const,
};

// Recipient capture-engagement (session activity) — /capture/recipient-engagement.
export const engagementKeys = {
  all: () => ['capture', 'recipient-engagement'] as const,
  recipient: (recipientId: string | undefined, campaignId?: string) =>
    [...engagementKeys.all(), recipientId ?? null, campaignId ?? 'all'] as const,
};

// NOTE: recipient-source integration connection state (LDAP / M365 / Google /
// Okta) is keyed by `integrationKeys` in components/settings/hooks/use-integrations.ts
// (the home of useIntegration, the reader). That factory is mode-keyed there;
// it is NOT duplicated here to avoid a conflicting export.

// Sender identities — shared picker used by LMS, Announcements, and the
// company-defaults pages (endpoint /announcements/sender-identities). Authed +
// tenant-scoped, so mode-keyed: a demo session lists the demo tenant's
// identities, not the real workspace's.
export const senderIdentitiesKeys = {
  all: () => ['sender-identities'] as const,
  list: () => [...senderIdentitiesKeys.all(), 'list'] as const,
};

export const onboardingKeys = {
  all: ['onboarding'] as const,
  status: () => [...onboardingKeys.all, 'status'] as const,
  checklist: () => [...onboardingKeys.all, 'checklist'] as const,
  prefill: () => [...onboardingKeys.all, 'prefill'] as const,
};

export const teamKeys = {
  all: ['team'] as const,
  lists: () => [...teamKeys.all, 'list'] as const,
  detail: (id: string) => [...teamKeys.all, 'detail', id] as const,
};

export const accessRequestKeys = {
  all: ['access-requests'] as const,
  lists: () => [...accessRequestKeys.all, 'list'] as const,
};

export const securityKeys = {
  all: ['security'] as const,
  settings: () => [...securityKeys.all, 'settings'] as const,
  sessions: () => [...securityKeys.all, 'sessions'] as const,
};

export const workspaceKeys = {
  all: ['workspaces'] as const,
  list: () => [...workspaceKeys.all, 'list'] as const,
};

export const domainKeys = {
  all: ['domains'] as const,
  // `all: true` marks the fetch-all (paged-through) variant so it caches
  // separately from the page-N/searchable list used by the settings card.
  list: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    all?: boolean;
  }) => [...domainKeys.all, 'list', params ?? {}] as const,
};

export const feedbackKeys = {
  all: ['feedback'] as const,
  lists: () => [...feedbackKeys.all, 'list'] as const,
  list: (filter?: Record<string, unknown>) => [...feedbackKeys.lists(), filter] as const,
  details: () => [...feedbackKeys.all, 'detail'] as const,
  detail: (id: string) => [...feedbackKeys.details(), id] as const,
  stats: () => [...feedbackKeys.all, 'stats'] as const,
  thread: (ticketId: string) => [...feedbackKeys.all, 'thread', ticketId] as const,
};

/**
 * Mode-aware feedback query keys — used by the `/feedback` table surface
 * (list / detail / timeline / stats / submitters). `mode` (ApiMode) is the
 * FIRST positional arg per the useApiModeStore gold-standard conformance
 * pattern (mirrors recipientKeys + lmsReportKeys). The legacy `feedbackKeys`
 * above is retained for the feedback-submission widget hooks that pre-date
 * the conformance work.
 */
export const feedbackTableKeys = {
  all: () => ['feedback'] as const,
  lists: () => [...feedbackTableKeys.all(), 'list'] as const,
  list: (filter?: Record<string, unknown>) =>
    [...feedbackTableKeys.lists(), filter] as const,
  details: () => [...feedbackTableKeys.all(), 'detail'] as const,
  detail: (id: string) =>
    [...feedbackTableKeys.details(), id] as const,
  timeline: (id: string) =>
    [...feedbackTableKeys.all(), 'timeline', id] as const,
  // Prefix key — invalidate this to clear EVERY filter-scoped stats variant.
  statsAll: () => [...feedbackTableKeys.all(), 'stats'] as const,
  stats: (filter?: Record<string, unknown>) =>
    [...feedbackTableKeys.statsAll(), filter] as const,
  submitters: () => [...feedbackTableKeys.all(), 'submitters'] as const,
  thread: (ticketId: string) =>
    [...feedbackTableKeys.all(), 'thread', ticketId] as const,
};

export const aiChatKeys = {
  all: ['ai-chat'] as const,
  conversations: () => [...aiChatKeys.all, 'conversations'] as const,
  conversationList: (search?: string) => [...aiChatKeys.conversations(), search] as const,
  conversation: (id: string) => [...aiChatKeys.conversations(), id] as const,
  settings: () => [...aiChatKeys.all, 'settings'] as const,
};

export const passkeyKeys = {
  all: ['passkeys'] as const,
  list: () => [...passkeyKeys.all, 'list'] as const,
  status: () => [...passkeyKeys.all, 'status'] as const,
};

export const companyKeys = {
  all: ['company'] as const,
  profile: () => [...companyKeys.all, 'profile'] as const,
  settings: () => [...companyKeys.all, 'settings'] as const,
};

export const billingKeys = {
  all: ['billing'] as const,
  subscription: () => [...billingKeys.all, 'subscription'] as const,
  usage: () => [...billingKeys.all, 'usage'] as const,
};

export const recycleBinKeys = {
  all: () => ['recycle-bin'] as const,
  lists: () => [...recycleBinKeys.all(), 'list'] as const,
  list: (filter?: Record<string, unknown>) =>
    [...recycleBinKeys.lists(), filter] as const,
};

export const profileKeys = {
  all: ['profile'] as const,
  me: () => [...profileKeys.all, 'me'] as const,
};

export const adminKeys = {
  all: ['admin'] as const,
  dashboard: () => [...adminKeys.all, 'dashboard'] as const,
  analyticsOverview: () => [...adminKeys.all, 'analytics', 'overview'] as const,
  companies: (filter?: Record<string, unknown>) => filter ? [...adminKeys.all, 'companies', filter] as const : [...adminKeys.all, 'companies'] as const,
  company: (id: string) => [...adminKeys.all, 'company', id] as const,
  workspaces: (filter?: Record<string, unknown>) => filter ? [...adminKeys.all, 'workspaces', filter] as const : [...adminKeys.all, 'workspaces'] as const,
  users: (filter?: Record<string, unknown>) => filter ? [...adminKeys.all, 'users', filter] as const : [...adminKeys.all, 'users'] as const,
  superAdmins: (filter?: Record<string, unknown>) => filter ? [...adminKeys.all, 'super-admins', filter] as const : [...adminKeys.all, 'super-admins'] as const,
  auditLogs: (filter?: Record<string, unknown>) => filter ? [...adminKeys.all, 'audit-logs', filter] as const : [...adminKeys.all, 'audit-logs'] as const,
  feedback: (filter?: Record<string, unknown>) => filter ? [...adminKeys.all, 'feedback', filter] as const : [...adminKeys.all, 'feedback'] as const,
  feedbackStats: () => [...adminKeys.all, 'feedback-stats'] as const,
  systemLogs: (filter?: Record<string, unknown>) => filter ? [...adminKeys.all, 'system-logs', filter] as const : [...adminKeys.all, 'system-logs'] as const,
  errorIssues: (filter?: Record<string, unknown>) => filter ? [...adminKeys.all, 'error-issues', filter] as const : [...adminKeys.all, 'error-issues'] as const,
  // Phase B Wave 1 of async-job-self-healing — operator UI keys.
  asyncJobs: (filter?: Record<string, unknown>) => filter ? [...adminKeys.all, 'async-jobs', filter] as const : [...adminKeys.all, 'async-jobs'] as const,
  asyncJob: (id: string) => [...adminKeys.all, 'async-jobs', 'detail', id] as const,
  asyncJobsCounts: () => [...adminKeys.all, 'async-jobs', 'counts'] as const,
  // Super-admin 2FA setup / verify / recovery flows.
  superAdmin2fa: {
    all: () => [...adminKeys.all, 'super-admin-2fa'] as const,
  },
  // Super-admin impersonation session audit log.
  impersonation: {
    sessions: (filters: Record<string, unknown> = {}) =>
      ['admin', 'impersonation', 'sessions', filters] as const,
  },
  // Admin Project (Verification Ledger) — 2026-05-25.
  // Module → Story → Task → Subtask hierarchy with status workflow.
  project: {
    all: () => [...adminKeys.all, 'project'] as const,
    modules: () => [...adminKeys.all, 'project', 'modules'] as const,
    module: (id: string) => [...adminKeys.all, 'project', 'modules', id] as const,
    board: (id: string) => [...adminKeys.all, 'project', 'modules', id, 'board'] as const,
    report: (id: string) => [...adminKeys.all, 'project', 'modules', id, 'report'] as const,
    story: (id: string) => [...adminKeys.all, 'project', 'stories', id] as const,
    task: (id: string) => [...adminKeys.all, 'project', 'tasks', id] as const,
    sizes: () => [...adminKeys.all, 'project', 'sizes'] as const,
  },
  // PR-3 — Super Admin sending-domain management.
  sendingDomains: (filter?: Record<string, unknown>) =>
    filter
      ? ([...adminKeys.all, 'sending-domains', filter] as const)
      : ([...adminKeys.all, 'sending-domains'] as const),
  sendingDomain: (id: string) =>
    [...adminKeys.all, 'sending-domains', 'detail', id] as const,
  // Module entitlements — registry + global defaults + per-company + per-workspace overrides.
  modulesRegistry: () => [...adminKeys.all, 'modules', 'registry'] as const,
  modulesGlobal: () => [...adminKeys.all, 'modules', 'global'] as const,
  modulesCompany: (companyId: string) =>
    [...adminKeys.all, 'modules', 'company', companyId] as const,
  modulesWorkspace: (companyId: string, workspaceId: string) =>
    [...adminKeys.all, 'modules', 'workspace', companyId, workspaceId] as const,
};

export const reportKeys = {
  all: ['reports'] as const,
  dashboard: (tenant: Record<string, unknown>) => [...reportKeys.all, 'dashboard', tenant] as const,
  entities: (tenant: Record<string, unknown>, filter?: Record<string, unknown>) => [...reportKeys.all, 'entities', tenant, filter] as const,
  entity: (id: string) => [...reportKeys.all, 'entity', id] as const,
  l3: (tenant: Record<string, unknown>, filter?: Record<string, unknown>) => [...reportKeys.all, 'l3', tenant, filter] as const,
  falsePositives: (tenant: Record<string, unknown>, filter?: Record<string, unknown>) => [...reportKeys.all, 'false-positives', tenant, filter] as const,
  anomalies: (tenant: Record<string, unknown>, filter?: Record<string, unknown>) => [...reportKeys.all, 'anomalies', tenant, filter] as const,
  analytics: (tenant: Record<string, unknown>, entityId?: string) => [...reportKeys.all, 'analytics', tenant, entityId] as const,
  users: (tenant: Record<string, unknown>, filter?: Record<string, unknown>) => [...reportKeys.all, 'users', tenant, filter] as const,
  settings: (tenant: Record<string, unknown>) => [...reportKeys.all, 'settings', tenant] as const,
};

export const smtpKeys = {
  all: ['smtp'] as const,
  list: () => [...smtpKeys.all, 'list'] as const,
  detail: (id: string) => [...smtpKeys.all, 'detail', id] as const,
};

export const riskBotKeys = {
  all: ['risk-bot'] as const,
  conversations: (search?: string) => [...riskBotKeys.all, 'conversations', search] as const,
  conversation: (id: string) => [...riskBotKeys.all, 'conversation', id] as const,
  messages: (id: string) => [...riskBotKeys.all, 'messages', id] as const,
};

export const entityKeys = {
  all: ['entities'] as const,
  lists: () => [...entityKeys.all, 'list'] as const,
  detail: (id: string) => [...entityKeys.all, 'detail', id] as const,
  stats: (id: string) => [...entityKeys.all, 'stats', id] as const,
  trend: (id: string, window: number, frequency: string) => [...entityKeys.all, 'trend', id, window, frequency] as const,
  artifacts: (id: string) => [...entityKeys.all, 'artifacts', id] as const,
  topRecipients: (id: string, limit: number) => [...entityKeys.all, 'top-recipients', id, limit] as const,
  leaderboard: () => [...entityKeys.all, 'leaderboard'] as const,
  derivationRules: (id: string) => [...entityKeys.all, 'derivation-rules', id] as const,
};

/**
 * Task B5.2c — scheduled executive-report email CRUD
 * (GET/POST /report-schedules, PATCH/DELETE /report-schedules/:id). Not
 * namespaced under entityKeys — report-schedules is a standalone resource
 * even though today's only reportType (executive_entity_leaderboard) reports
 * on entities.
 */
export const reportScheduleKeys = {
  all: ['report-schedules'] as const,
  lists: () => [...reportScheduleKeys.all, 'list'] as const,
};

// View preferences (Redis-backed per-user, per-workspace list-view state).
// `mode` is first positional arg so Live↔Demo toggling invalidates correctly —
// Demo session uses a different workspaceId on the BE, so the cached blob is
// naturally isolated, but the FE cache still needs separate keys to avoid
// hydrating Live UI from a Demo entry mid-toggle.
export const viewPreferenceKeys = {
  all: () => ['view-preferences'] as const,
  byView: (viewKey: string) =>
    [...viewPreferenceKeys.all(), viewKey] as const,
};
